'use client';

import { createContext, useContext, ReactNode } from 'react';
import { useSession, signOut } from 'next-auth/react';
import { useRouter } from 'next/navigation';

export interface User {
  id: string;
  name: string;
  phone: string;
  countryCode?: string;
  email?: string;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: () => void; // Now redirects to login page
  logout: () => Promise<void>;
  isLoading: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: session, status } = useSession();
  const router = useRouter();
  const isLoading = status === 'loading';

  // Map NextAuth session to our User type
  const user: User | null = session?.user
    ? {
        id: session.user.id || '',
        name: session.user.name || '',
        phone: session.user.phone || '',
        countryCode: session.user.countryCode,
        email: session.user.email || undefined,
      }
    : null;

  // Redirect to login page
  const login = () => {
    router.push('/login');
  };

  // Sign out via NextAuth
  const logout = async () => {
    await signOut({ callbackUrl: '/' });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!session?.user,
        login,
        logout,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
